/**
 * Redux thunks for the site scheduling calendar.
 *
 * Wraps the odl_app calendar HTTP layer. Reads pull the whole event series
 * (recurrence definitions) and the server-expanded occurrences for a visible
 * date window. Writes keep a single create/edit/delete vocabulary for the view
 * layer and route by `edit_type` internally: an occurrence-scoped edit or delete
 * hits the occurrence RPC routes, everything else the series CRUD routes.
 *
 * Contains:
 * - loadCalendarSeries / fetchCalendarOccurrences — reads
 * - createEvent / editEvent / deleteEvent — writes (series vs occurrence by edit_type)
 */
import { createAsyncThunk } from '@reduxjs/toolkit';

import * as CalendarApi from '@sophocles/api/calendar';

/** Transport keys carried on a write payload but not part of the event body. */
const TRANSPORT_KEYS = ['siteId', 'id', 'edit_type', 'occurrence_date'];

const eventFields = payload => {
  const fields = { ...payload };
  TRANSPORT_KEYS.forEach(key => delete fields[key]);
  return fields;
};

export const loadCalendarSeries = createAsyncThunk('calendar/loadSeries', async ({ siteId }, { rejectWithValue }) => {
  try {
    const data = await CalendarApi.fetchCalendarSeries(siteId);
    return data.events ?? [];
  } catch (e) {
    return rejectWithValue(e.response?.data);
  }
});

export const fetchCalendarOccurrences = createAsyncThunk(
  'calendar/fetchOccurrences',
  async ({ siteId, start, end, relay }, { rejectWithValue }) => {
    try {
      return await CalendarApi.fetchCalendarOccurrences(siteId, { start, end, relay });
    } catch (e) {
      return rejectWithValue(e.response?.data);
    }
  },
);

export const createEvent = createAsyncThunk(
  'calendar/createEvent',
  async ({ siteId, ...data }, { rejectWithValue }) => {
    try {
      return await CalendarApi.createCalendarEvent(siteId, data);
    } catch (e) {
      return rejectWithValue(e.response?.data);
    }
  },
);

export const editEvent = createAsyncThunk('calendar/editEvent', async (payload, { rejectWithValue }) => {
  const { siteId, id, edit_type: editType, occurrence_date: occurrenceDate } = payload;
  try {
    if (editType === 'occurrence') {
      return await CalendarApi.createCalendarOccurrenceOverride(siteId, id, {
        occurrence_date: occurrenceDate,
        ...eventFields(payload),
      });
    }
    return await CalendarApi.updateCalendarEventSeries(siteId, id, eventFields(payload));
  } catch (e) {
    return rejectWithValue(e.response?.data);
  }
});

export const deleteEvent = createAsyncThunk('calendar/deleteEvent', async (payload, { rejectWithValue }) => {
  const { siteId, eventId, edit_type: editType, occurrence_date: occurrenceDate } = payload;
  try {
    if (editType === 'occurrence') {
      return await CalendarApi.deleteCalendarOccurrence(siteId, eventId, occurrenceDate);
    }
    return await CalendarApi.deleteCalendarEventSeries(siteId, eventId);
  } catch (e) {
    return rejectWithValue(e.response?.data);
  }
});
